import { ASSIGNED_SEATING } from "@shared/constants";
import { isTicketTypeWaitlisted } from "@shared/modules/ticket-types/utils/ticket-type-utils";
import { useTranslation } from "@shared/utils/internationalization";
import { toCapitalizedWord } from "@shared/utils/text/utils";
import isEmpty from "lodash/isEmpty";
import { useMemo } from "react";
import { TooltipReturnType } from "../../services/SeatingMapService";
import { SeatStatus } from "../../types/seating-types";

const MAX_ARRAY_LENGTH = 5;

/**
 * This hook stores variables used to display information about tickets/memberships/etc on seating map tooltips.
 * Only up to MAX_ARRAY_LENGTH items will be displayed, as we don't want the tooltip to overflow the screen.
 */
export function useMapTooltip(info: TooltipReturnType) {
  const { t } = useTranslation("seating");
  const tooltipId = "seating-tooltip";

  // controls if "Sec/Row/Seat" or "Sec/Area" text is displayed on the tooltip
  const isGALocation =
    info?.details?.name === ASSIGNED_SEATING.GENERAL_ADMISSION.ID;

  const isWaitlist =
    !isEmpty(info?.itemTypes) &&
    info?.itemTypes.every((ig) => isTicketTypeWaitlisted(ig));

  // controls if the "Click to see X more items" text should be shown
  const hasMoreItems = info?.itemTypes?.length > MAX_ARRAY_LENGTH;
  // the number displayed in the "Click to see X more items" text
  const hasMoreCount = info?.itemTypes?.length - MAX_ARRAY_LENGTH;
  // the first X items in the item array, which will be displayed in full on the tooltip
  // (returns the entire array if length is smaller than X)
  const items = info?.itemTypes?.slice(0, MAX_ARRAY_LENGTH) ?? [];

  const headerVals = useMemo(() => {
    // Displays the segment name
    if (info?.details?.segmentText) {
      return [{ id: 0, title: "", value: info?.details?.segmentText }];
    }
    // Displays "WAITLIST" text
    if (isWaitlist) {
      return [{ id: 0, title: "", value: t("Waitlist").toUpperCase() }];
    }
    // Displays "Sec/Area" text
    if (isGALocation) {
      return [
        {
          id: 0,
          title: t("Sec"),
          value: info?.details?.segment_identifier,
        },
        {
          id: 1,
          title: t("Area"),
          value: info?.details?.identifier,
        },
      ];
    }

    // Displays "Sec/Row/Seat" text
    return [
      { id: 0, title: t("Sec"), value: info?.details?.segment_identifier },
      {
        id: 1,
        title: t("Row"),
        value: info?.details?.location_identifier ?? "",
      },
      { id: 1, title: t("Seat"), value: info?.details?.identifier },
    ];
  }, [info?.details, isGALocation]);

  const purchaserDetails = info?.details?.purchaser;

  const status = info?.details?.status;
  let headerLabel = "";
  let statusLabel = "";
  if (status) {
    headerLabel =
      status === SeatStatus.SOLD || Boolean(purchaserDetails?.invoice_id)
        ? t("Purchaser")
        : t("Held for");
    statusLabel =
      status === SeatStatus.RESERVED ? t("On-Hold") : toCapitalizedWord(status);
  }

  return {
    tooltipId,
    hasMoreCount,
    hasMoreItems,
    isGALocation,
    items,
    headerVals,
    purchaserDetails,
    status,
    headerLabel,
    statusLabel,
  };
}
